// Run-status presentation helpers, shared across the Runs table, the Run
// detail page, and the per-pipeline recent-runs panel. The canonical status
// list mirrors SYNC_LOG_STATUSES in packages/db/src/schema/sync-log.ts.

export type RunBadgeTone = "ok" | "warn" | "fail" | "muted";

/** Every status a sync_log row can carry, in pipeline-progression order. */
export const RUN_STATUSES = [
  "received",
  "parsing",
  "transforming",
  "looking_up",
  "delivering",
  "completed",
  "skipped",
  "conflict",
  "failed",
] as const;

export type RunStatus = (typeof RUN_STATUSES)[number];

const TERMINAL = new Set<string>(["completed", "skipped", "conflict", "failed"]);

/**
 * Map a run status to an ANANSI badge tone. Stays within the four palette tones
 * — no rainbow. completed = success tint (ok); failed = error tint (fail);
 * skipped/conflict = muted; everything in-flight = warn (accent outline).
 */
export function runTone(status: string): RunBadgeTone {
  switch (status) {
    case "completed":
      return "ok";
    case "failed":
      return "fail";
    case "skipped":
    case "conflict":
      return "muted";
    default:
      // received / parsing / transforming / looking_up / delivering
      return "warn";
  }
}

/** True once a run has reached a terminal state. */
export function isTerminal(status: string): boolean {
  return TERMINAL.has(status);
}

/** The reason a run failed or was skipped, if any. */
export function runReason(run: {
  status: string;
  errorMessage: string | null;
  skipReason: string | null;
}): string | null {
  if (run.status === "failed") return run.errorMessage ?? null;
  if (run.status === "skipped" || run.status === "conflict") return run.skipReason ?? null;
  return run.errorMessage ?? run.skipReason ?? null;
}
